import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { getUserInfo, suspendUser, reactivateUser, deleteUser, addTokens } from "../../helpers/userInfoApi";

const UserInfoModal = ({ isOpen, toggle, userId, onUpdate, reportCount, firstReportedAt, lastReportedAt }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [reactivateModal, setReactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [addTokensModal, setAddTokensModal] = useState(false);
  const [tokenAmount, setTokenAmount] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchUserInfo = useCallback(async () => {
    if (!userId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    try {
      const result = await getUserInfo(userId);
      
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setUser(result.user);
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching user info:", error);
        setUser(null);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [userId]);

  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen && userId) {
      fetchUserInfo();
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, userId, fetchUserInfo]);

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      alert("Please provide a reason for suspension");
      return;
    }

    setActionLoading(true);
    try {
      await suspendUser({
        user_id: userId,
        reason: suspendReason.trim(),
      });

      // Refresh user info
      await fetchUserInfo();
      setSuspendModal(false);
      setSuspendReason("");
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error suspending user:", error);
      alert(error.response?.data?.message || error.message || "Failed to suspend user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await reactivateUser({
        user_id: userId,
      });

      // Refresh user info
      await fetchUserInfo();
      setReactivateModal(false);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error reactivating user:", error);
      alert(error.response?.data?.message || error.message || "Failed to reactivate user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmation.trim()) {
      alert("Please type 'DELETE' to confirm deletion");
      return;
    }

    if (deleteConfirmation.trim().toUpperCase() !== "DELETE") {
      alert("Confirmation text must be exactly 'DELETE'");
      return;
    }

    setActionLoading(true);
    try {
      await deleteUser({
        userId: userId,
      });

      // Close modal and notify parent
      setDeleteModal(false);
      setDeleteConfirmation("");
      toggle(); // Close the main modal
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.response?.data?.message || error.message || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddTokens = async () => {
    if (!tokenAmount || isNaN(tokenAmount) || parseInt(tokenAmount) <= 0) {
      alert("Please enter a valid positive number for tokens");
      return;
    }

    if (!userId) {
      alert("User ID is missing");
      return;
    }

    setActionLoading(true);
    try {
      await addTokens({
        tokens: parseInt(tokenAmount),
        userIds: [userId],
      });

      // Refresh user info
      await fetchUserInfo();
      setAddTokensModal(false);
      setTokenAmount("");
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error adding tokens:", error);
      alert(error.response?.data?.message || error.message || "Failed to add tokens");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleClose = () => {
    setUser(null);
    setSuspendReason("");
    setDeleteConfirmation("");
    setSuspendModal(false);
    setReactivateModal(false);
    setDeleteModal(false);
    toggle();
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" className="user-info-modal">
        <ModalHeader className="bg-primary text-white" toggle={handleClose}>
          <div className="d-flex align-items-center">
            <i className="mdi mdi-account me-2"></i>
            <span>User Information</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }} className="p-3">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3 text-muted">Loading user information...</p>
            </div>
          ) : !user ? (
            <div className="text-center py-5">
              <i className="mdi mdi-account-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
              <p className="mt-3 text-muted">User not found</p>
            </div>
          ) : (
            <>
              {/* Profile Header */}
              <Card className="mb-3 shadow-sm border-primary">
                <CardBody className="p-3">
                  <Row className="g-3 align-items-center">
                    {user.basicInfo?.primaryImgURL && (
                      <Col xs="auto">
                        <img
                          src={user.basicInfo.primaryImgURL}
                          alt={user.username}
                          className="rounded-circle"
                          style={{ width: "80px", height: "80px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/80";
                          }}
                        />
                      </Col>
                    )}
                    <Col>
                      <h5 className="mb-1 fw-bold">{user.username || "N/A"}</h5>
                      <div className="text-muted small font-monospace mb-2">{user.userId}</div>
                      <div className="d-flex gap-2 flex-wrap">
                        {user.phoneVerified && (
                          <Badge color="success" className="px-2 py-1">
                            <i className="mdi mdi-check-circle me-1"></i>Phone Verified
                          </Badge>
                        )}
                        {user.age && (
                          <Badge color="info" className="px-2 py-1">
                            Age: {user.age}
                          </Badge>
                        )}
                        {user.basicInfo?.gender && (
                          <Badge color="secondary" className="px-2 py-1">
                            Gender: {user.basicInfo.gender === "1" ? "Male" : user.basicInfo.gender === "2" ? "Female" : "Other"}
                          </Badge>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* User Bio */}
              {user.userBio && (
                <Card className="mb-3 shadow-sm">
                  <CardBody className="p-3">
                    <h6 className="mb-2 fw-bold">
                      <i className="mdi mdi-text me-2"></i>Bio
                    </h6>
                    <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{user.userBio}</p>
                  </CardBody>
                </Card>
              )}

              {/* Basic Information */}
              <Card className="mb-3 shadow-sm">
                <CardBody className="p-3">
                  <h6 className="mb-3 fw-bold">
                    <i className="mdi mdi-information me-2"></i>Basic Information
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">User ID</label>
                        <div className="font-monospace small">{user.userId}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Username</label>
                        <div className="fw-semibold">{user.username || "N/A"}</div>
                      </div>
                    </Col>
                    {user.age && (
                      <Col md={6}>
                        <div className="mb-2">
                          <label className="form-label text-muted small mb-1">Age</label>
                          <div>{user.age}</div>
                        </div>
                      </Col>
                    )}
                    {user.basicInfo?.gender && (
                      <Col md={6}>
                        <div className="mb-2">
                          <label className="form-label text-muted small mb-1">Gender</label>
                          <div>
                            {user.basicInfo.gender === "1" ? "Male" : 
                             user.basicInfo.gender === "2" ? "Female" : 
                             user.basicInfo.gender}
                          </div>
                        </div>
                      </Col>
                    )}
                    {user.basicInfo?.favoriteEmoji && (
                      <Col md={6}>
                        <div className="mb-2">
                          <label className="form-label text-muted small mb-1">Favorite Emoji</label>
                          <div style={{ fontSize: "1.5rem" }}>{user.basicInfo.favoriteEmoji}</div>
                        </div>
                      </Col>
                    )}
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Phone Verified</label>
                        <div>
                          <Badge color={user.phoneVerified ? "success" : "danger"}>
                            {user.phoneVerified ? "Yes" : "No"}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Report Information (if provided) */}
              {(reportCount !== undefined || firstReportedAt || lastReportedAt) && (
                <Card className="mb-3 shadow-sm border-danger">
                  <CardBody className="p-3 bg-danger-subtle">
                    <h6 className="mb-3 fw-bold text-danger">
                      <i className="mdi mdi-flag me-2"></i>Report Information
                    </h6>
                    <Row className="g-3">
                      {reportCount !== undefined && (
                        <Col md={4}>
                          <div className="mb-2">
                            <label className="form-label text-muted small mb-1">Report Count</label>
                            <div>
                              <Badge color="danger" className="px-3 py-2">
                                {reportCount} {reportCount === 1 ? "Report" : "Reports"}
                              </Badge>
                            </div>
                          </div>
                        </Col>
                      )}
                      {firstReportedAt && (
                        <Col md={4}>
                          <div className="mb-2">
                            <label className="form-label text-muted small mb-1">First Reported At</label>
                            <div className="small">{formatDate(firstReportedAt)}</div>
                          </div>
                        </Col>
                      )}
                      {lastReportedAt && (
                        <Col md={4}>
                          <div className="mb-2">
                            <label className="form-label text-muted small mb-1">Last Reported At</label>
                            <div className="small">{formatDate(lastReportedAt)}</div>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* Timeline */}
              <Card className="mb-3 shadow-sm">
                <CardBody className="p-3">
                  <h6 className="mb-3 fw-bold">
                    <i className="mdi mdi-clock-outline me-2"></i>Timeline
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Created At</label>
                        <div className="small">{formatDate(user.created_at)}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Updated At</label>
                        <div className="small">{formatDate(user.updated_at)}</div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          {user && (
            <>
              <Button
                color="info"
                onClick={() => setAddTokensModal(true)}
                className="px-4"
              >
                <i className="mdi mdi-token me-2"></i>Add Tokens
              </Button>
              <Button
                color="warning"
                onClick={() => setSuspendModal(true)}
                className="px-4"
              >
                <i className="mdi mdi-pause-circle me-2"></i>Suspend User
              </Button>
              <Button
                color="success"
                onClick={() => setReactivateModal(true)}
                className="px-4"
              >
                <i className="mdi mdi-play-circle me-2"></i>Reactivate User
              </Button>
              <Button
                color="danger"
                onClick={() => setDeleteModal(true)}
                className="px-4"
              >
                <i className="mdi mdi-delete me-2"></i>Delete User
              </Button>
            </>
          )}
          <Button color="secondary" onClick={handleClose} className="px-4">
            <i className="mdi mdi-close me-2"></i>Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Suspend User Modal */}
      <Modal isOpen={suspendModal} toggle={() => setSuspendModal(false)} centered>
        <ModalHeader toggle={() => setSuspendModal(false)} className="bg-danger text-white">
          <i className="mdi mdi-alert-circle me-2"></i>Suspend User
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Are you sure you want to suspend this user?</p>
            {user && (
              <small className="text-muted">User: {user.username} ({user.userId})</small>
            )}
          </div>
          <FormGroup>
            <Label>Reason for Suspension <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows="4"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter the reason for suspending this user..."
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSuspendModal(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleSuspend} disabled={actionLoading || !suspendReason.trim()}>
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Suspending...
              </>
            ) : (
              <>
                <i className="mdi mdi-alert-circle me-2"></i>Suspend
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reactivate User Modal */}
      <Modal isOpen={reactivateModal} toggle={() => setReactivateModal(false)} centered>
        <ModalHeader toggle={() => setReactivateModal(false)} className="bg-success text-white">
          <i className="mdi mdi-check-circle me-2"></i>Reactivate User
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Are you sure you want to reactivate this user?</p>
            {user && (
              <small className="text-muted">User: {user.username} ({user.userId})</small>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setReactivateModal(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button color="success" onClick={handleReactivate} disabled={actionLoading}>
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Reactivating...
              </>
            ) : (
              <>
                <i className="mdi mdi-check-circle me-2"></i>Reactivate
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete User Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalHeader toggle={() => setDeleteModal(false)} className="bg-danger text-white">
          <i className="mdi mdi-alert-circle me-2"></i>Delete User
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="alert alert-danger" role="alert">
              <i className="mdi mdi-alert-circle me-2"></i>
              <strong>Warning!</strong> This action cannot be undone. Deleting a user will permanently remove all associated data.
            </div>
            {user && (
              <div className="mb-3">
                <p className="mb-1">Are you sure you want to delete this user?</p>
                <p className="mb-0">
                  <strong>User:</strong> {user.username} ({user.userId})
                </p>
              </div>
            )}
            <FormGroup>
              <Label>
                Type <strong className="text-danger">DELETE</strong> to confirm:
              </Label>
              <Input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE to confirm"
                className={deleteConfirmation && deleteConfirmation.trim().toUpperCase() !== "DELETE" ? "border-danger" : ""}
              />
              {deleteConfirmation && deleteConfirmation.trim().toUpperCase() !== "DELETE" && (
                <small className="text-danger">
                  <i className="mdi mdi-alert-circle me-1"></i>
                  Confirmation text must be exactly "DELETE"
                </small>
              )}
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setDeleteModal(false);
            setDeleteConfirmation("");
          }} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            color="danger" 
            onClick={handleDelete} 
            disabled={actionLoading || deleteConfirmation.trim().toUpperCase() !== "DELETE"}
          >
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              <>
                <i className="mdi mdi-delete me-2"></i>Delete User
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Tokens Modal */}
      <Modal isOpen={addTokensModal} toggle={() => setAddTokensModal(false)} centered>
        <ModalHeader toggle={() => setAddTokensModal(false)} className="bg-info text-white">
          <i className="mdi mdi-token me-2"></i>Add Tokens
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Add tokens to this user:</p>
            {user && (
              <small className="text-muted">User: {user.username} ({user.userId})</small>
            )}
          </div>
          <FormGroup>
            <Label>Token Amount <span className="text-danger">*</span></Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={tokenAmount}
              onChange={(e) => setTokenAmount(e.target.value)}
              placeholder="Enter number of tokens to add"
              required
            />
            <small className="text-muted">Enter a positive number of tokens to add to this user.</small>
          </FormGroup>
          {tokenAmount && !isNaN(tokenAmount) && parseInt(tokenAmount) > 0 && (
            <div className="alert alert-info mb-0">
              <i className="mdi mdi-information me-2"></i>
              <strong>Confirm:</strong> You are about to add <strong>{parseInt(tokenAmount)}</strong> token{parseInt(tokenAmount) !== 1 ? 's' : ''} to this user.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setAddTokensModal(false);
            setTokenAmount("");
          }} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            color="info" 
            onClick={handleAddTokens} 
            disabled={actionLoading || !tokenAmount || isNaN(tokenAmount) || parseInt(tokenAmount) <= 0}
          >
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding Tokens...
              </>
            ) : (
              <>
                <i className="mdi mdi-token me-2"></i>Add Tokens
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default UserInfoModal;

